"use client"

import { useEffect, useRef } from "react"
import { animateChildren } from "@/lib/animation-utils"
import { Check, AlertCircle } from "lucide-react"

interface FormSummaryProps {
  data: any
}

export default function FormSummaryStep({ data }: FormSummaryProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      // Set initial state for children
      Array.from(containerRef.current.children).forEach((child) => {
        ;(child as HTMLElement).style.opacity = "0"
        ;(child as HTMLElement).style.transform = "translateY(20px)"
      })

      // Animate children with staggered delay
      requestAnimationFrame(() => {
        if (containerRef.current) {
          animateChildren(
            containerRef.current,
            {
              opacity: 1,
              translateY: 0,
            },
            {
              duration: 400,
              easing: "ease-out",
            },
          )
        }
      })
    }
  }, [])

  const sections = [
    {
      title: "Cow Identification",
      fields: [
        { label: "Cow Ear Tag", value: data.cowEarTag },
        { label: "Cow Management", value: data.cowManagement },
        { label: "Cow Breed", value: data.cowBreed },
      ],
    },
    {
      title: "Reproduction Cycle",
      fields: [
        { label: "Sync Year", value: data.syncYear },
        { label: "Sync Month", value: data.syncMonth },
        { label: "Estrous Cycle", value: data.estrousCycle || "N/A" },
        { label: "Body Condition Score (BCS)", value: data.bcs },
        { label: "Pregnancy Diagnosis", value: data.pregnancyDiagnosis },
      ],
    },
    {
      title: "Pregnancy Results",
      fields: [
        { label: "Final Pregnancy Check", value: data.finalPregnancyCheck },
        { label: "Observations", value: data.observationFinalPregnancyCheck || "N/A" },
      ],
    },
    {
      title: "Calf Birth Details",
      fields: [
        { label: "Birth Year", value: data.calfBirthYear },
        { label: "Birth Month", value: data.calfBirthMonth },
        { label: "Calf Number", value: data.calfNumber },
        { label: "Calf Sex", value: data.calfSex },
        { label: "Coat Color", value: data.calfCoatColor },
        { label: "Birth Weight", value: data.calfBirthWeight + " kg" },
        { label: "Calf Type", value: data.calfType },
      ],
    },
    {
      title: "Calf Development",
      fields: [
        { label: "Gestation Period", value: data.gestationPeriod + " days" },
        { label: "Birth Notes", value: data.calfBirthNotes || "N/A" },
        { label: "Death Date", value: data.calfDeathDate || "N/A" },
        { label: "Death Notes", value: data.calfDeathNotes || "N/A" },
      ],
    },
    {
      title: "Weaning Information",
      fields: [
        { label: "Weaning Date", value: data.weaningDate || "N/A" },
        { label: "Weaning Weight", value: data.weaningWeight ? data.weaningWeight + " kg" : "N/A" },
        { label: "Weaning Location", value: data.weaningLocation || "N/A" },
      ],
    },
    {
      title: "Physical Assessment",
      fields: [
        { label: "Tick Count", value: data.tickCount || "N/A" },
        { label: "Head Evaluation", value: data.headEvaluation || "N/A" },
        { label: "Body Type", value: data.bodyType || "N/A" },
        { label: "Color Pattern", value: data.color || "N/A" },
        { label: "Muscle Development", value: data.muscleDevelopment || "N/A" },
        { label: "Umbilical Condition", value: data.umbilicalCondition || "N/A" },
        { label: "Body Structure", value: data.bodyStructure || "N/A" },
        { label: "General Conformation", value: data.generalConformation || "N/A" },
        { label: "Ear Tag Verified", value: data.earTagVerified ? "Yes" : "No" },
      ],
    },
  ]

  return (
    <div ref={containerRef} className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-slate-900">Summary</h2>
        <p className="text-slate-500">Please review all information before submitting</p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-center gap-2 text-amber-800">
          <AlertCircle className="h-5 w-5" />
          <p className="text-sm font-medium">Please verify all information is correct before submitting.</p>
        </div>
      </div>

      <div className="space-y-6">
        {sections.map((section, index) => (
          <div key={index} className="rounded-lg border p-4">
            <h3 className="mb-3 font-medium text-slate-900">{section.title}</h3>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                  <div>
                    <span className="text-sm font-medium text-slate-700">{field.label}:</span>{" "}
                    <span className="text-sm text-slate-600">{field.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
